// tennis-spinner.component.ts
// time logged - 15 hrs 8.16 
import { Component, Input, OnInit, OnDestroy, ViewEncapsulation, ElementRef, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-tennis-spinner',
  imports: [],
  templateUrl: './tennis-spinner.component.html',
  styleUrl: './tennis-spinner.component.css',
  encapsulation: ViewEncapsulation.Emulated
})
export class TennisSpinnerComponent implements OnInit, OnDestroy {
  @Input() size: 'small' | 'medium' | 'large' | 'xlarge' = 'medium';
  @Input() theme: 'light' | 'dark' = 'light';
  @Input() speed: number = 1000; // Animation speed in ms
  @Input() autoStart: boolean = true;
  @Input() showText: boolean = true;
  @Input() loadingText: string = 'Loading...';
  @Input() logoUrl: string = ''; // URL for owl logo image
  @Input() logoWidth: number = 40; // Logo width
  @Input() logoHeight: number = 40; // Logo height
  @Input() logoPosition: { x: number; y: number } = { x: -30, y: 105 }; // Position on court - DO NOT TOUCH SKEW

  @ViewChild('spinnerContainer', { static: false }) spinnerContainer!: ElementRef;

  svgContent: SafeHtml = '';
  private animationRunning: boolean = false;
  private animationInterval: any = null;
  private currentDirection: 'forward' | 'backward' = 'forward';
  private progress: number = 0;
  private uniqueId: string;

  private ball: SVGCircleElement | null = null;
  private ballShadow: SVGEllipseElement | null = null;
  private robot1Arm: SVGGElement | null = null;
  private robot2Arm: SVGGElement | null = null;

  constructor(private sanitizer: DomSanitizer) {
    this.uniqueId = `spinner-${Math.random().toString(36).substring(2, 11)}`;
  }

  ngOnInit(): void {
    this.createSVG();
    if (this.autoStart) {
      // Small delay to ensure DOM is ready
      setTimeout(() => this.start(), 100);
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private createSVG(): void {

    //cant split this out
    const svg = `
      <svg viewBox="0 0 600 500" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="topGradient-${this.uniqueId}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#0066b2;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#004d8c;stop-opacity:1" />
          </linearGradient>
          
          <linearGradient id="leftGradient-${this.uniqueId}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:#003d70;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#002d52;stop-opacity:1" />
          </linearGradient>
          
          <linearGradient id="rightGradient-${this.uniqueId}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:#004d8c;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#003d70;stop-opacity:1" />
          </linearGradient>

          <linearGradient id="robotGradient1-${this.uniqueId}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#8ba8b8;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#047CB1;stop-opacity:1" />
          </linearGradient>

          <linearGradient id="robotGradient2-${this.uniqueId}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#f87171;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#ef4444;stop-opacity:1" />
          </linearGradient>

          <radialGradient id="ballGradient-${this.uniqueId}">
            <stop offset="0%" style="stop-color:#fef08a;stop-opacity:1" />
            <stop offset="70%" style="stop-color:#facc15;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#eab308;stop-opacity:1" />
          </radialGradient>

          <!-- Mask for logo to appear behind net but in front of court -->
          <mask id="courtMask-${this.uniqueId}">
            <rect x="-300" y="-250" width="900" height="750" fill="white"/>
            <path d="M -65,88 L 25,42 L 25,62 L -65,108 Z" fill="black" transform="translate(300, 220)"/>
          </mask>
        </defs>

        <g transform="translate(300, 220) scale(1.475)">
          <!-- Tennis Court -->
          <g>
            <path d="M -180,60 L -180,80 L 0,170 L 0,150 Z" 
                  fill="url(#leftGradient-${this.uniqueId})" 
                  stroke="#064e3b" 
                  stroke-width="1"/>
            
            <path d="M 0,150 L 0,170 L 120,110 L 120,90 Z" 
                  fill="url(#rightGradient-${this.uniqueId})" 
                  stroke="#047857" 
                  stroke-width="1"/>
            
            <path d="M -180,60 L -60,0 L 120,90 L 0,150 Z" 
                  fill="url(#topGradient-${this.uniqueId})" 
                  stroke="#008916" 
                  stroke-width="2"/>



            <!-- Court lines (on top of logo) -->
            <g>
              <line x1="-120" y1="30" x2="0" y2="90" 
                    stroke="#ffffff" 
                    stroke-width="1.5" 
                    opacity="0.5"/>
              <line x1="-60" y1="60" x2="60" y2="120" 
                    stroke="#ffffff" 
                    stroke-width="1.5" 
                    opacity="0.5"/>
            </g>
            
            <!-- Logo on court surface (bottom right section) -->
            <image xlink:href="/assets/images/logos/owl-at-the-open-240x100.png" 
                   href="/assets/images/logos/owl-at-the-open-240x100.png"
                   x="32" 
                   y="120" 
                   width="60" 
                   height="25" 
                   opacity="0.8"
                   class="court-logo"
                   transform="rotate(-25 62 132) skewX(-15)"
                   onerror="console.log('Image failed to load')"/>
                   
            <!-- Second logo on top court surface -->
            <image xlink:href="/assets/images/logos/Owl-surface-120x100.png" 
                   href="/assets/images/logos/Owl-surface-120x100.png"
                   x="62" 
                   y="157" 
                   width="45" 
                   height="32" 
                   opacity="0.8"
                   class="court-logo"
                   transform="rotate(30 84.5 173) skewX(-30)"/>
            
            <!-- Net (on top of everything) -->
            <g>
              <path d="M -65,88 L 25,42 L 25,62 L -65,108 Z" 
                    fill="#e5e7eb" 
                    opacity="0.4"/>
              <line x1="-65" y1="108" x2="25" y2="62" 
                    stroke="#9ca3af" 
                    stroke-width="1.5" 
                    opacity="0.3"/>
              <line x1="-65" y1="88" x2="25" y2="42" 
                    stroke="#ffffff" 
                    stroke-width="2" 
                    opacity="0.7"/>
            </g>
          </g>

          <!-- Robot 1 (Blue) -->
          <g id="robot1-${this.uniqueId}" transform="translate(-120, 30)">
            <ellipse cx="0" cy="20" rx="12" ry="6" fill="#000000" opacity="0.2"/>
            <g id="robot1-body-${this.uniqueId}">
              <path d="M -10,-10 L 10,-10 L 10,10 L -10,10 Z" 
                    fill="url(#robotGradient1-${this.uniqueId})" 
                    stroke="#036499" 
                    stroke-width="1"/>
              <circle cx="0" cy="-15" r="8" 
                      fill="url(#robotGradient1-${this.uniqueId})" 
                      stroke="#036499" 
                      stroke-width="1"/>
              <circle cx="-3" cy="-15" r="2" fill="#ffffff"/>
              <circle cx="3" cy="-15" r="2" fill="#ffffff"/>
              <g id="robot1-arm-${this.uniqueId}">
                <line x1="10" y1="0" x2="20" y2="-5" 
                      stroke="#036499" 
                      stroke-width="3" 
                      stroke-linecap="round"/>
                <ellipse cx="22" cy="-7" rx="6" ry="8" 
                         fill="none" 
                         stroke="#1e293b" 
                         stroke-width="2"/>
              </g>
            </g>
          </g>

          <!-- Robot 2 (Red) -->
          <g id="robot2-${this.uniqueId}" transform="translate(70, 85)">
            <ellipse cx="0" cy="20" rx="12" ry="6" fill="#000000" opacity="0.2"/>
            <g id="robot2-body-${this.uniqueId}">
              <path d="M -10,-10 L 10,-10 L 10,10 L -10,10 Z" 
                    fill="url(#robotGradient1-${this.uniqueId})" 
                    stroke="#036499" 
                    stroke-width="1"/>
              <circle cx="0" cy="-15" r="8" 
                      fill="url(#robotGradient1-${this.uniqueId})" 
                      stroke="#036499" 
                      stroke-width="1"/>
              <circle cx="-3" cy="-15" r="2" fill="#ffffff"/>
              <circle cx="3" cy="-15" r="2" fill="#ffffff"/>
              <g id="robot2-arm-${this.uniqueId}">
                <line x1="-10" y1="0" x2="-20" y2="-5" 
                      stroke="#036499" 
                      stroke-width="3" 
                      stroke-linecap="round"/>
                <ellipse cx="-22" cy="-7" rx="6" ry="8" 
                         fill="none" 
                         stroke="#1e293b" 
                         stroke-width="2"/>
              </g>
            </g>
          </g>

          <!-- Ball -->
          <g>
            <ellipse id="ballShadow-${this.uniqueId}" cx="-100" cy="25" rx="4" ry="2" 
                     fill="#000000" 
                     opacity="0.2"/>
            <circle id="ball-${this.uniqueId}" cx="-100" cy="15" r="5" 
                    fill="url(#ballGradient-${this.uniqueId})"/>
          </g>
        </g>
      </svg>
    `;

    this.svgContent = this.sanitizer.bypassSecurityTrustHtml(svg);

    // Get references after view updates
    setTimeout(() => {
      if (this.spinnerContainer) {
        const container = this.spinnerContainer.nativeElement;
        this.ball = container.querySelector(`#ball-${this.uniqueId}`);
        this.ballShadow = container.querySelector(`#ballShadow-${this.uniqueId}`);
        this.robot1Arm = container.querySelector(`#robot1-arm-${this.uniqueId}`);
        this.robot2Arm = container.querySelector(`#robot2-arm-${this.uniqueId}`);
      }
    }, 0);
  }

  start(): void {
    if (this.animationRunning) return;
    this.animationRunning = true;
    this.animate();
  }

  stop(): void {
    this.animationRunning = false;
    if (this.animationInterval) {
      clearInterval(this.animationInterval);
      this.animationInterval = null;
    }
  }

  private animate(): void {
    const steps = 120;
    const stepDuration = this.speed / steps;

    this.animationInterval = setInterval(() => {
      if (!this.animationRunning || !this.ball || !this.ballShadow) {
        clearInterval(this.animationInterval);
        return;
      }

      if (this.currentDirection === 'forward') {
        this.progress += 1 / steps;
        if (this.progress >= 1) {
          this.progress = 1;
          this.currentDirection = 'backward';
          this.animateRobotHit(this.robot2Arm, 'backward');
        }
      } else {
        this.progress -= 1 / steps;
        if (this.progress <= 0) {
          this.progress = 0;
          this.currentDirection = 'forward';
          this.animateRobotHit(this.robot1Arm, 'forward');
        }
      }

      this.updateBallPosition();
    }, stepDuration);
  }

  /**
   * CORE BALL PHYSICS animation engine - handles realistic ball trajectory
   * research ball physics fine tune 8.01,
   * should create realistic parabolic motion with proper court surface interaction
   */
  private updateBallPosition(): void {
    // Defensive programming - bail if DOM elements aren't ready yet
    if (!this.ball || !this.ballShadow) return;

    // Court positioning system - these coordinates define the tennis court boundaries
    // Spent considerable time mapping the isometric court perspective to match realistic proportions
    const x1 = -100, y1 = 15;    // Robot 1 starting position (left side baseline)
    const peakX = -15, peakY = -30; // Arc peak position (net height simulation) 
    const bx1 = -60, by1 = 50;    // Ball bounce point 1 far court
    const bx2 = 30, by2 = 85;     // Ball bounce point 2 near court
    const x2 = 70, y2 = 70;       // Robot 2 ending position near court baseline

    let x, y; // Current ball position coordinates

    /**
     * FORWARD DIRECTION PHYSICS (https://beziercurvegenerator.vercel.app)
     * two-phase trajectory system that mimics ball flight
     * Phase 1: Quadratic Bezier curve from robot1->peak->bounce point
     * Phase 2: Linear trajectory with sine wave bounce simulation
     */
    if (this.currentDirection === 'forward') {
      if (this.progress <= 0.7) {
        // Phase 1: High arc trajectory using quadratic Bezier calcs
        // This creates the realistic parabolic flight path you see in professional tennis
        const t = this.progress / 0.7; // Normalize progress for this phase
        
        // Quadratic Bezier formula: dont understand but works
        //
        x = (1 - t) * (1 - t) * x1 + 2 * (1 - t) * t * peakX + t * t * bx2;
        y = (1 - t) * (1 - t) * y1 + 2 * (1 - t) * t * peakY + t * t * by2;
      } else {
        // Phase 2: post bounce trajectory with realistic bounce physics
        const t = (this.progress - 0.7) / 0.3; // Normalize remaining 30% of journey
        
        x = bx2 + (x2 - bx2) * t; // linear horizontal movement
        // vertical movement with sine wave bounce - amplitude decreases as ball approaches robot2
        y = by2 + (y2 - by2) * t - Math.sin(t * Math.PI) * 12;
      }
    } else {
      /**
       * BACKWARD DIRECTION PHYSICs - 
       * Mirror image of forward trajectory but with reverse phase timing
       * This ensures symmetrical ball movement regardless of direction
       */
      if (this.progress >= 0.3) {
        // Phase 1: High arc trajectory (stick to 70% of reverse journey)
        const t = (1 - this.progress) / 0.7;
        
        // Same Bezier math but with reversed start/end points
        x = (1 - t) * (1 - t) * x2 + 2 * (1 - t) * t * peakX + t * t * bx1;
        y = (1 - t) * (1 - t) * y2 + 2 * (1 - t) * t * peakY + t * t * by1;
      } else {
        // Phase 2: Post bounce trajectory (the final 30% of reverse journey)
        const t = this.progress / 0.3;
        
        x = bx1 + (x1 - bx1) * (1 - t);
        y = by1 + (y1 - by1) * (1 - t) - Math.sin((1 - t) * Math.PI) * 12;
      }
    }

    // Apply calculated position to ball and shadow DOM elements
    this.ball.setAttribute('cx', x.toString());
    this.ball.setAttribute('cy', y.toString());
    this.ballShadow.setAttribute('cx', x.toString()); // Shadow follows ball horizontally

    /**
     * COURT SURFACE CALCS
     * Determines the court surface Y-coordinate at any given X position (isometric view extremely tricky - I DID NOT PLAY W/ SKEW)
     * This is critical for realistic shadow positioning and depth perception
     * The court has two distinct slopes that meet at the net line (x = -15)
     */
    let courtSurfaceY;
    if (x <= -15) {
      // Left side of court - calculate surface height using linear interpolation
      const t = (x - x1) / (-15 - x1); // Interpolation factor for back court section
      courtSurfaceY = y1 + (65 - y1) * t; // 65 is the court surface at net line - to reconsider postioning
    } else {
      // Right side of court - different slope calculation
      const t = (x - (-15)) / (x2 - (-15)); // Interpolation factor for front court section
      courtSurfaceY = 65 + (y2 - 65) * t;
    }

    // Position shadow on court surface with slight offset for realism
    this.ballShadow.setAttribute('cy', (courtSurfaceY + 15).toString());

    /**
     * DEPTH SIM
     * Creates realistic ball size scaling and shadow effects based on height above court
     * This system took hours to calibrate for the most natural visual effect
     */
    const heightAboveCourt = Math.abs(y - courtSurfaceY);
    
    // Ball size scaling - higher balls appear smaller due to perspective (also consider change to ball shadow)
    const size = 5 - heightAboveCourt * 0.01; // Subtle size reduction with height
    this.ball.setAttribute('r', Math.max(3, size).toString()); // Minimum size constraint
    
    // Shadow size scaling - shadows get smaller as ball gets higher - Huge improvement 8.09 commit this
    const shadowSize = 4 - heightAboveCourt * 0.02; // More aggressive scaling for shadows
    this.ballShadow.setAttribute('rx', Math.max(2, shadowSize).toString());
    this.ballShadow.setAttribute('ry', Math.max(1, shadowSize / 2).toString()); // Elliptical shadow
    
    // Shadow opacity - higher balls cast fainter shadows (lighting physics)
    const shadowOpacity = 0.2 - heightAboveCourt * 0.0015;
    this.ballShadow.setAttribute('opacity', Math.max(0.05, shadowOpacity).toString());
  }

  /**
   * ROBOT ARM ANIMATIONSS 
   * Simulates racket swing mechanics with arm motion
   * Each robot has mirrored arm movement - simplist implementation
   * Transform origin is  calculated to create natural arm rotation
   */
  private animateRobotHit(robotArm: SVGGElement | null, direction: 'forward' | 'backward'): void {
    if (!robotArm) return; // Guard clause for safety

    // Set rotation pivot point based on bots orientation
    // Forward = right robot swinging left-handed, Backward = left robot swinging right-handed
    robotArm.style.transformOrigin = direction === 'forward' ? '10px 0px' : '-10px 0px';
    
    // Smooth CSS transition for natural swing motion - 200ms feels most realistic
    robotArm.style.transition = 'transform 0.2s';
    
    // Execute the swing - 15 degree rotation 
    robotArm.style.transform = direction === 'forward' ? 'rotate(-15deg)' : 'rotate(15deg)';

    // Return arm to neutral position after swing completes
    // Timing matches the CSS transition duration for seamless animation
    setTimeout(() => {
      robotArm.style.transform = 'rotate(0deg)';
    }, 200);
  }
}
